import React from "react";
import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {zodResolver} from "@hookform/resolvers/zod";
import {FormProvider, useForm} from "react-hook-form";
import {toast} from "sonner";
import {useCreatePaymentFlow} from "../../../apis/requests/payment-flows/createPaymentFlow";
import {useGetPaymentFlowFeatureFlags} from "../../../apis/requests/payment-flows/getPaymentFlowFeatureFlags";
import {useGetPaymentFlows} from "../../../apis/requests/payment-flows/getPaymentFlows";
import {useGetPaymentFlowsSchema} from "../../../apis/requests/payment-flows/getPaymentFlowsSchema";
import {CARD_PAYMENTS_RETRY_FLOW_TYPE} from "../../../apis/requests/payment-flows/types";
import {useUpdatePaymentFlow} from "../../../apis/requests/payment-flows/updatePaymentFlow";
import {firstErrorMessage} from "../../segments/segment-form/helpers";
import ConditionsSection from "../flow-form/ConditionsSection";
import {EMPTY_CONDITION_GROUP} from "../flow-form/constants";
import DetailsSection from "../flow-form/DetailsSection";
import FeatureFlagSection from "../flow-form/FeatureFlagSection";
import FlowDialog from "../flow-form/FlowDialog";
import {findDefaultFlow, getNewFlowPriority, withCurrentFlag} from "../flow-form/helpers";
import {isDefaultFlow} from "../helpers";
import {buildPaymentFlowPayload, buildVariantsFromFlag, flowToForm} from "./retry-test-form/helpers";
import {formSchema, FormValues} from "./retry-test-form/schema";
import VariantsSection from "./retry-test-form/VariantsSection";

const NEW_TITLE = "New retry test";
const EDIT_TITLE = "Edit retry test";
const CREATED_TEXT = "Retry test created";
const SAVED_TEXT = "Retry test saved";
const NO_FLAGS_HINT = "A developer needs to add one in code before you can create a retry test.";
const CONDITIONS_DESCRIPTION =
  "Define which card payments this test applies to. A group with no rules is ignored; leave every group empty to match all transactions.";

const EMPTY_FORM: FormValues = {
  name: "",
  feature_flag: "",
  group_match_mode: "all",
  groups: [EMPTY_CONDITION_GROUP],
  variants: [],
};

type Props = {
  flowId?: number;
};

const RetryTestDialog = NiceModal.create(({flowId}: Props) => {
  const isEdit = flowId !== undefined;
  const modal = useModal();
  const {data: flows = []} = useGetPaymentFlows({type: CARD_PAYMENTS_RETRY_FLOW_TYPE});
  const {data: featureFlags = [], isLoading: isLoadingFlags, isError: isFlagsError} = useGetPaymentFlowFeatureFlags();
  const {data: schema, isError: isSchemaError} = useGetPaymentFlowsSchema();
  const parameters = schema?.parameters ?? [];
  const {mutateAsync: createAsync, isPending: isCreating} = useCreatePaymentFlow();
  const {mutateAsync: updateAsync, isPending: isUpdating} = useUpdatePaymentFlow();
  const isBusy = isCreating || isUpdating;
  const existing = isEdit ? flows.find((flow) => flow.id === flowId) : undefined;

  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: EMPTY_FORM,
    values: existing ? flowToForm(existing) : undefined,
  });
  const {
    handleSubmit,
    setValue,
    formState: {errors, isSubmitted, isDirty},
  } = methods;
  const issue = isSubmitted ? firstErrorMessage(errors) : null;

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (existing) {
        await updateAsync({id: existing.id, payload: buildPaymentFlowPayload(values, parameters)});
      } else {
        await createAsync({...buildPaymentFlowPayload(values, parameters), priority: getNewFlowPriority(flows)});
      }
      toast.success(isEdit ? SAVED_TEXT : CREATED_TEXT);
      modal.hide();
    } catch {}
  });

  return (
    <FlowDialog
      title={isEdit ? EDIT_TITLE : NEW_TITLE}
      isEdit={isEdit}
      isBusy={isBusy}
      isDirty={isDirty}
      issue={issue}
      onSubmit={onSubmit}
    >
      <FormProvider {...methods}>
        <DetailsSection />
        <FeatureFlagSection
          featureFlags={withCurrentFlag(featureFlags, existing)}
          isLoading={isLoadingFlags}
          isError={isFlagsError}
          noFlagsHint={NO_FLAGS_HINT}
          onFlagChange={(flag) =>
            setValue("variants", flag ? buildVariantsFromFlag(flag, findDefaultFlow(flows)) : [], {shouldDirty: true})
          }
        />
        <ConditionsSection
          description={CONDITIONS_DESCRIPTION}
          parameters={parameters}
          isError={isSchemaError}
          isLocked={!!existing && isDefaultFlow(existing)}
        />
        <VariantsSection flows={flows} />
      </FormProvider>
    </FlowDialog>
  );
});

export default RetryTestDialog;
